import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTrash } from "react-icons/fa";
import "./addCities.css";
import Swal from "sweetalert2";
import MobileNav from "../../components/mobileNav/MobileNav";

const AddCities = () => {
  const [groupedData, setGroupedData] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchRegions = async () => {
    try {
      const response = await axios.get(
        "https://api.generalhome.sa/api/MaintenanceCity/GetMaintenanceMainCities"
      );
      setRegions(response.data);
    } catch (error) {
      toast.error("❌ حدث خطأ أثناء تحميل المناطق");
      console.error("Error fetching regions:", error);
    }
  };

  const GetMaintenanceCities = () => {
    axios
      .get(
        "https://api.generalhome.sa/api/MaintenanceCity/GetMaintenanceCities"
      )
      .then((response) => {
        const rawData = response.data;

        // Group data by MainCity first
        const groupedByMainCity = rawData.reduce((acc, area) => {
          area.cities.forEach((city) => {
            const mainCityId = city.MainCity?.Id || "unknown";
            const mainCityName = city.MainCity?.NameMainCity || "غير معروف";

            if (!acc[mainCityId]) {
              acc[mainCityId] = {
                mainCityId,
                mainCityName,
                areas: [],
              };
            }

            // Check if this area already exists for this main city
            const existingArea = acc[mainCityId].areas.find(
              (a) => a.MaintenanceAreaId === area.MaintenanceAreaId
            );

            if (!existingArea) {
              acc[mainCityId].areas.push({
                MaintenanceAreaId: area.MaintenanceAreaId,
                MaintenanceAreaName: area.MaintenanceAreaName,
                cities: [city],
              });
            } else {
              existingArea.cities.push(city);
            }
          });
          return acc;
        }, {});

        // Convert to array format
        const structuredData = Object.values(groupedByMainCity);

        // Add static areas if they don't already exist
        const staticAreas = [
          { id: 1, name: "الرياض" },
          { id: 2, name: "القصيم" },
        ];

        setGroupedData(structuredData);
        setAreaOptions(staticAreas);
        setLoading(false);
      })
      .catch(() => {
        toast.error("❌ حدث خطأ أثناء تحميل البيانات");
        setLoading(false);
      });
  };

  const handleDeleteCity = async (Id) => {
    const result = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من التراجع عن هذا الإجراء!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "نعم، احذفها!",
      cancelButtonText: "إلغاء",
      reverseButtons: true,
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `https://api.generalhome.sa/api/MaintenanceCity/DeleteMaintenanceCity/${Id}`
        );
        await Swal.fire("تم الحذف!", "تم حذف المدينة بنجاح.", "success");
        GetMaintenanceCities(); // Refresh the data
      } catch (error) {
        await Swal.fire("خطأ!", "حدث خطأ أثناء محاولة حذف المدينة.", "error");
        console.error("Error deleting city:", error);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      regionId: "",
      cityName: "",
      selectedAreaId: "",
    },
    validationSchema: Yup.object({
      regionId: Yup.string().required("اسم المنطقة مطلوب"),
      cityName: Yup.string().required("اسم المدينة مطلوب"),
      selectedAreaId: Yup.string().required("يرجى اختيار المنطقة"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      const { cityName, selectedAreaId, regionId } = values;

      // Find if city already exists in any area
      const isDuplicate = groupedData.some((mainCity) =>
        mainCity.areas.some((area) =>
          area.cities.some((city) => city.Name.trim() === cityName.trim())
        )
      );

      if (isDuplicate) {
        toast.warning("⚠️ هذه المدينة موجودة بالفعل في هذه المنطقة");
        setSubmitting(false);
        return;
      }

      axios
        .post(
          "https://api.generalhome.sa/api/MaintenanceCity/CreateMaintenanceCity",
          {
            Id: 0,
            Name: cityName.trim(),
            MainCityId: parseInt(regionId),
            MaintenanceAreaId: parseInt(selectedAreaId),
          }
        )
        .then(() => {
          toast.success("✅ تم إضافة المدينة بنجاح");
          resetForm();
          GetMaintenanceCities();
        })
        .catch(() => {
          toast.error("❌ حدث خطأ أثناء إضافة المدينة");
        })
        .finally(() => setSubmitting(false));
    },
  });

  useEffect(() => {
    fetchRegions();
    GetMaintenanceCities();
  }, []);

  return (
    <>
      <MobileNav />
      <div className="add-cities-container">
        <h2 className="mb-3">مناطق الصيانة والمدن التابعة</h2>
        <ToastContainer />

        {loading ? (
          <p className="loader">جارٍ تحميل البيانات...</p>
        ) : (
          <>
            <form className="add-city-form" onSubmit={formik.handleSubmit}>
              <h3>➕ إضافة مدينة جديدة</h3>

              <div className="form-group">
                <label>اسم المنطقة:</label>
                <select
                  name="regionId"
                  value={formik.values.regionId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">اختر المنطقة</option>
                  {regions.map((region) => (
                    <option key={region.Id} value={region.Id}>
                      {region.NameMainCity}
                    </option>
                  ))}
                </select>
                {formik.touched.regionId && formik.errors.regionId && (
                  <p className="error">{formik.errors.regionId}</p>
                )}
              </div>

              <div className="form-group">
                <label>اسم المدينة:</label>
                <input
                  type="text"
                  name="cityName"
                  value={formik.values.cityName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="مثال: بريدة"
                />
                {formik.touched.cityName && formik.errors.cityName && (
                  <p className="error">{formik.errors.cityName}</p>
                )}
              </div>

              <div className="form-group">
                <label>اختر منطقة الصيانة:</label>
                <select
                  name="selectedAreaId"
                  value={formik.values.selectedAreaId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">اختر منطقة الصيانه</option>
                  {areaOptions.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
                {formik.touched.selectedAreaId &&
                  formik.errors.selectedAreaId && (
                    <p className="error">{formik.errors.selectedAreaId}</p>
                  )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? "جارٍ الحفظ..." : "حفظ المدينة"}
              </button>
            </form>

            <div className="main-cities-container">
              {groupedData.map((mainCity) => (
                <div key={mainCity.mainCityId} className="main-city-card">
                  <h3 className="main-city-title">
                    🏙️ {mainCity.mainCityName}
                  </h3>
                  <div className="areas-container">
                    {mainCity.areas.map((area) => (
                      <div key={area.MaintenanceAreaId} className="area-card">
                        <h4 className="area-name">
                          📍 {area.MaintenanceAreaName}
                        </h4>
                        <ul className="city-list">
                          {area.cities.map((city) => (
                            <li key={city.Id} className="city-item">
                              <span>{city.Name}</span>
                              <button
                                className="delete-city-btn"
                                onClick={() => handleDeleteCity(city.Id)}
                                title="حذف المدينة"
                              >
                                <FaTrash />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AddCities;
